import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Badge from '../../components/ui/Badge';
import {
  CheckCircleIcon,
  ChevronDownIcon,
  CountChip,
  DocumentTextIcon,
  DownloadIcon,
  EyeIcon,
  FolderIcon,
  GeneralList,
  GeneralListSortableTh,
  PencilIcon,
  PlusIcon,
  Select,
  TrashIcon,
  UndoIcon,
  useConfirm,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../components/ui';
import { formatDateTime } from '../../config/appConfig';
import { ChartTrendingUpIcon } from '../../components/ui/Icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTopbar } from '../../contexts/TopbarContext';
import { cn } from '../../lib/utils';
import {
  deleteEFormSubmission,
  exportEFormTableLayout,
  revertEFormSubmissionToDraft,
  submitEFormSubmission,
} from '../../services/mutations';
import type { ListQueryOptions } from '../../services/paginatedList';
import { ApprovalNoticeIcon } from './ApprovalNoticeIcon';
import {
  flattenSchemaFields,
  formatFieldDisplayValue,
  coerceEFormFiles,
  isEFormSchema,
  resolveFilterFields,
  resolveListColumns,
  type EFormFieldSchema,
  type EFormSchema,
} from './schema';
import { openEFormFile } from './FileField';
import {
  EFORM_REFERENCE_SOURCES,
  referenceLabelsForField,
  useEFormReferenceLabelMaps,
} from './referenceSources';
import {
  EFormDefinition,
  EFormSubmission,
  type EFormPrintTemplate,
  type EFormSavedReport,
  getPublishedVersion,
  submitterDisplayName,
  useEFormDefinition,
  useEFormPrintLayout,
  useEFormSavedReports,
  useEFormSubmissions,
  usePublishedEForms,
} from './useData';
import { listPrintTemplates, printModeLabel } from './printLayoutModel';

function schemaForDefinition(def: EFormDefinition | undefined): EFormSchema | null {
  if (!def) return null;
  const pub = getPublishedVersion(def);
  const draft = def.versions?.find((v) => v.status === 'DRAFT');
  const schema = pub?.schemaJson ?? draft?.schemaJson;
  if (!schema || !isEFormSchema(schema)) return null;
  return schema;
}

function schemaFieldsForDefinition(def: EFormDefinition | undefined): EFormFieldSchema[] {
  const schema = schemaForDefinition(def);
  if (!schema) return [];
  return flattenSchemaFields(schema);
}

type StatusTab = 'all' | 'DRAFT' | 'SUBMITTED';

const statusTabs: Array<{ id: StatusTab; label: string; icon: React.ReactNode }> = [
  { id: 'all', label: 'All', icon: <DocumentTextIcon className="h-4 w-4" /> },
  { id: 'DRAFT', label: 'Draft', icon: <FolderIcon className="h-4 w-4" /> },
  { id: 'SUBMITTED', label: 'Submitted', icon: <CheckCircleIcon className="h-4 w-4" /> },
];

const FormIcon = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12h6m-6 4h6M7 4h10a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2zm3 4h.01M10 12h.01M10 16h.01"
    />
  </svg>
);

type ListColumn =
  | { kind: 'meta'; key: 'id' | 'status' | 'approval' | 'submitter' | 'submittedAt'; header: string; sortable?: boolean }
  | { kind: 'field'; key: string; header: string; field: EFormFieldSchema };

const SubmissionsList: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { can } = useAuth();
  const { showSuccess, showError } = useNotification();
  const confirm = useConfirm();
  const { setTitleOverride } = useTopbar();
  const navigate = useNavigate();
  const { definitionId: definitionIdParam } = useParams<{ definitionId?: string }>();
  const [searchParams] = useSearchParams();
  const canEdit = can('e_form_submissions', 'edit');
  const canDelete = can('e_form_submissions', 'delete');

  const definitionId = useMemo(() => {
    if (definitionIdParam && /^\d+$/.test(definitionIdParam)) {
      return parseInt(definitionIdParam, 10);
    }
    const q = searchParams.get('definitionId');
    if (q && /^\d+$/.test(q)) return parseInt(q, 10);
    return null;
  }, [definitionIdParam, searchParams]);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState<StatusTab>('all');
  const [fieldFilters, setFieldFilters] = useState<Record<string, string>>({});
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const { sortConfig, handleSort } = useGeneralListSort(
    { key: 'submittedAt', direction: 'desc' },
    {
      defaultDirectionForKey: (key) =>
        key === 'submittedAt' || key === 'createdAt' || key === 'id' ? 'desc' : 'asc',
    },
  );

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
    setSearchInput('');
    setSearch('');
    setTab('all');
    setFieldFilters({});
  }, [definitionId]);

  useEffect(() => {
    setPage(1);
  }, [search, tab, itemsPerPage, sortConfig.key, sortConfig.direction, fieldFilters]);

  useEffect(() => {
    if (definitionIdParam) return;
    const q = searchParams.get('definitionId');
    if (q && /^\d+$/.test(q)) {
      navigate(`/e-forms/${q}/submissions`, { replace: true });
    }
  }, [definitionIdParam, searchParams, navigate]);

  const published = usePublishedEForms(undefined, 1, 200);
  const definitionDetail = useEFormDefinition(definitionId ?? 0);
  const savedReports = useEFormSavedReports(definitionId ?? null);
  const printLayout = useEFormPrintLayout(definitionId);
  const printouts = useMemo(() => {
    const all = listPrintTemplates(printLayout.data?.printLayoutJson);
    return all.filter((template) => template.outputMode && template.outputMode !== 'none');
  }, [printLayout.data?.printLayoutJson]);
  const [printingTable, setPrintingTable] = useState(false);

  const runTablePrint = async (template: EFormPrintTemplate) => {
    if (definitionId == null) return;
    setPrintingTable(true);
    try {
      await exportEFormTableLayout(definitionId, template.id);
      showSuccess(`Printed “${template.name}”.`);
    } catch (error: any) {
      showError(error?.response?.data?.message || error?.message || 'Could not print');
    } finally {
      setPrintingTable(false);
    }
  };
  const selectedDef =
    definitionDetail.data ?? published.data.find((d) => d.id === definitionId);

  useEffect(() => {
    if (definitionId == null) {
      setTitleOverride('Form submissions');
      return () => setTitleOverride(undefined);
    }
    const name = selectedDef?.name ?? `Form #${definitionId}`;
    setTitleOverride(name);
    return () => setTitleOverride(undefined);
  }, [definitionId, selectedDef?.name, setTitleOverride]);

  const formSchema = useMemo(() => {
    const fromDef = schemaForDefinition(selectedDef);
    if (fromDef) return fromDef;
    return null;
  }, [selectedDef]);

  const fieldColumns = useMemo(() => schemaFieldsForDefinition(selectedDef), [selectedDef]);

  const filterFieldKeys = useMemo(
    () => (formSchema ? resolveFilterFields(formSchema) : []),
    [formSchema],
  );

  const filterFields = useMemo(() => {
    if (!formSchema || !filterFieldKeys.length) return [] as EFormFieldSchema[];
    const byKey = new Map(flattenSchemaFields(formSchema).map((f) => [f.key, f]));
    return filterFieldKeys.map((k) => byKey.get(k)).filter(Boolean) as EFormFieldSchema[];
  }, [formSchema, filterFieldKeys]);

  const referenceFields = useMemo(() => {
    const fromSchema = formSchema ? flattenSchemaFields(formSchema) : fieldColumns;
    const nested = fromSchema.flatMap((f) => f.fields ?? []);
    return [...fromSchema, ...nested];
  }, [formSchema, fieldColumns]);
  const referenceLabelMaps = useEFormReferenceLabelMaps(referenceFields);

  const listOptions = useMemo((): ListQueryOptions => {
    const filters: Record<string, string[]> = {};
    if (tab === 'DRAFT' || tab === 'SUBMITTED') filters.status = [tab];
    for (const [key, value] of Object.entries(fieldFilters)) {
      if (value) filters[key] = [value];
    }
    return {
      sortKey: sortConfig.key,
      sortDirection: sortConfig.direction,
      filters: Object.keys(filters).length ? { filters } : undefined,
    };
  }, [tab, sortConfig.key, sortConfig.direction, fieldFilters]);

  const submissions = useEFormSubmissions(
    definitionId,
    search || undefined,
    page,
    itemsPerPage,
    listOptions,
  );

  const totalPages = Math.max(1, Math.ceil((submissions.total ?? 0) / itemsPerPage));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const listSchema = useMemo(() => {
    if (formSchema) return formSchema;
    if (submissions.data[0]?.schemaSnapshot && isEFormSchema(submissions.data[0].schemaSnapshot)) {
      return submissions.data[0].schemaSnapshot;
    }
    return null;
  }, [formSchema, submissions.data]);

  const formatCell = (field: EFormFieldSchema, raw: unknown) =>
    formatFieldDisplayValue(field, raw, {
      referenceLabels: referenceLabelsForField(field, referenceLabelMaps),
    });

  const listColumns = useMemo((): ListColumn[] => {
    let fields = fieldColumns;
    if (
      !fields.length &&
      submissions.data[0]?.schemaSnapshot &&
      isEFormSchema(submissions.data[0].schemaSnapshot)
    ) {
      fields = flattenSchemaFields(submissions.data[0].schemaSnapshot);
    }
    const fieldsByKey = new Map(fields.map((f) => [f.key, f]));
    const resolved = listSchema
      ? resolveListColumns(listSchema)
      : [
          { key: 'id', visible: true },
          { key: 'status', visible: true },
          { key: 'approval', visible: true },
          { key: 'submitter', visible: true },
          { key: 'submittedAt', visible: true },
          ...fields.map((f) => ({ key: f.key, visible: true })),
        ];

    const metaHeaders: Record<string, { header: string; sortable?: boolean }> = {
      id: { header: 'ID', sortable: true },
      status: { header: 'Status', sortable: true },
      approval: { header: 'Approval' },
      submitter: { header: 'Submitted by' },
      submittedAt: { header: 'Date', sortable: true },
    };

    const result: ListColumn[] = [];
    for (const col of resolved) {
      if (!col.visible) continue;
      if (
        col.key === 'id' ||
        col.key === 'status' ||
        col.key === 'approval' ||
        col.key === 'submitter' ||
        col.key === 'submittedAt'
      ) {
        result.push({
          kind: 'meta',
          key: col.key,
          header: metaHeaders[col.key].header,
          sortable: metaHeaders[col.key].sortable,
        });
        continue;
      }
      const field = fieldsByKey.get(col.key);
      if (!field) continue;
      result.push({ kind: 'field', key: field.key, header: field.label, field });
    }
    return result;
  }, [fieldColumns, listSchema, submissions.data]);

  const hasFieldFilters = Object.values(fieldFilters).some(Boolean);
  const hasFilters = Boolean(search.trim() || tab !== 'all' || hasFieldFilters);
  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setTab('all');
    setFieldFilters({});
  };

  /** Prefer lookup source name (e.g. Vessels) over the palette default "Dropdown". */
  const filterAllLabel = (field: EFormFieldSchema): string => {
    if (field.optionsSource === 'reference' && field.referenceKey) {
      const src = EFORM_REFERENCE_SOURCES.find((s) => s.key === field.referenceKey);
      if (src?.label) return `All ${src.label}`;
    }
    const raw = (field.label || '').trim();
    const name =
      raw && raw.toLowerCase() !== 'dropdown' && raw.toLowerCase() !== 'radio'
        ? raw
        : (field.key || 'options').trim();
    return `All ${name}`;
  };

  const filterOptionsFor = (field: EFormFieldSchema): Array<{ value: string; label: string }> => {
    const all = { value: '', label: filterAllLabel(field) };
    if (field.optionsSource === 'reference' && field.referenceKey) {
      const map = referenceLabelMaps.get(field.referenceKey);
      const opts = map
        ? Array.from(map.entries())
            .map(([value, label]) => ({ value, label }))
            .sort((a, b) => a.label.localeCompare(b.label))
        : [];
      return [all, ...opts];
    }
    const opts = (field.options ?? []).map((o) => ({
      value: String(o.value),
      label: o.label || String(o.value),
    }));
    return [all, ...opts];
  };

  const runStatusAction = async (
    e: React.MouseEvent,
    row: EFormSubmission,
    action: 'submit' | 'revert',
  ) => {
    e.stopPropagation();
    const label = `#${row.id}`;
    const ok = await confirm(
      action === 'submit'
        ? {
            title: 'Submit form',
            message: `Submit ${label}? Submitted records cannot be edited until reverted to draft.`,
            confirmText: 'Submit',
            cancelText: 'Cancel',
          }
        : {
            title: 'Revert to draft',
            message: `Revert ${label} to draft? You will be able to edit it again.`,
            confirmText: 'Revert',
            cancelText: 'Cancel',
            confirmVariant: 'danger',
          },
    );
    if (!ok) return;
    try {
      if (action === 'submit') await submitEFormSubmission(row.id);
      else await revertEFormSubmissionToDraft(row.id);
      showSuccess(action === 'submit' ? 'Submission submitted' : 'Reverted to draft');
      await submissions.mutate();
    } catch (err: unknown) {
      const ex = err as { response?: { data?: { message?: string } }; message?: string };
      const msg = ex?.response?.data?.message || ex?.message;
      showError(
        typeof msg === 'string'
          ? msg
          : action === 'submit'
            ? 'Submit failed'
            : 'Revert failed',
      );
    }
  };

  const handleDelete = async (e: React.MouseEvent, row: EFormSubmission) => {
    e.stopPropagation();
    const ok = await confirm({
      title: 'Delete submission',
      message: `Delete #${row.id} permanently? This cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteEFormSubmission(row.id);
      await submissions.mutate();
      showSuccess('Submission deleted');
    } catch (err: unknown) {
      const ex = err as { response?: { data?: { message?: string } }; message?: string };
      showError(ex?.response?.data?.message || ex?.message || 'Delete failed');
    }
  };

  const handleExport = async () => {
    const { exportToExcel } = await import('../../components/exports');
    const exportCols = listColumns.map((c) => ({
      key: c.key,
      header: c.header,
    }));
    const rows = submissions.data.map((row) => {
      const flat: Record<string, unknown> = {};
      const schema = isEFormSchema(row.schemaSnapshot) ? row.schemaSnapshot : null;
      for (const col of listColumns) {
        if (col.kind === 'meta') {
          if (col.key === 'id') flat.id = row.id;
          else if (col.key === 'status') flat.status = row.status;
          else if (col.key === 'approval') {
            flat.approval = row.approval
              ? row.approval.status === 'PENDING' && row.approval.currentStepLabel
                ? `Pending — ${row.approval.currentStepLabel}`
                : row.approval.status
              : '';
          } else if (col.key === 'submitter') flat.submitter = submitterDisplayName(row);
          else if (col.key === 'submittedAt') flat.submittedAt = row.submittedAt ?? row.createdAt;
        } else {
          const schemaField = schema
            ? flattenSchemaFields(schema).find((x) => x.key === col.key)
            : col.field;
          flat[col.key] = formatCell(
            schemaField ?? col.field,
            (row.valuesJson ?? {})[col.key],
          );
        }
      }
      return flat;
    });
    await exportToExcel({
      sortedData: rows,
      tableColumns: exportCols,
      entityName: selectedDef?.name ?? 'Submissions',
      exportFileName: `${selectedDef?.code ?? 'eform'}-submissions`,
      formatDate: (d) => (d ? formatDateTime(d) : ''),
    });
  };

  const renderCell = (col: ListColumn, row: EFormSubmission) => {
    if (col.kind === 'meta') {
      if (col.key === 'id') {
        return <span className={cn('tabular-nums', colors.textMuted)}>#{row.id}</span>;
      }
      if (col.key === 'status') {
        return (
          <Badge variant={row.status === 'SUBMITTED' ? 'success' : 'warning'}>
            {row.status === 'SUBMITTED' ? 'Submitted' : 'Draft'}
          </Badge>
        );
      }
      if (col.key === 'approval') {
        const approval = row.approval;
        if (!approval) {
          return <span className={colors.textMuted}>—</span>;
        }
        const variant =
          approval.status === 'APPROVED'
            ? 'success'
            : approval.status === 'REJECTED' || approval.status === 'CANCELLED'
              ? 'danger'
              : approval.status === 'PENDING'
                ? 'warning'
                : 'default';
        const label =
          approval.status === 'PENDING'
            ? 'Pending'
            : approval.status === 'APPROVED'
              ? 'Approved'
              : approval.status === 'REJECTED'
                ? 'Rejected'
                : approval.status === 'CANCELLED'
                  ? 'Cancelled'
                  : approval.status === 'DRAFT'
                    ? 'Draft'
                    : approval.status;
        return (
          <Link
            to={`/approvals/${approval.id}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex flex-col items-start gap-0.5 min-w-0 max-w-[12rem]"
            title={approval.notice ? undefined : approval.title}
          >
            <span className="inline-flex items-center gap-1 min-w-0">
              <Badge variant={variant}>{label}</Badge>
              <ApprovalNoticeIcon approval={approval} />
            </span>
            {approval.status === 'PENDING' && approval.currentStepLabel ? (
              <span className={cn('text-[10px] truncate w-full', colors.textMuted)}>
                {approval.currentStepLabel}
              </span>
            ) : null}
          </Link>
        );
      }
      if (col.key === 'submitter') {
        return <span className="font-medium truncate block">{submitterDisplayName(row)}</span>;
      }
      return (
        <span className={colors.textSecondary}>
          {row.submittedAt
            ? formatDateTime(String(row.submittedAt))
            : row.createdAt
              ? formatDateTime(row.createdAt)
              : '—'}
        </span>
      );
    }

    const schema = isEFormSchema(row.schemaSnapshot) ? row.schemaSnapshot : null;
    const schemaField = schema
      ? flattenSchemaFields(schema).find((f) => f.key === col.key)
      : col.field;
    const effective = schemaField ?? col.field;
    const raw = (row.valuesJson ?? {})[col.key];
    const files =
      effective.type === 'file' ||
      effective.type === 'photo' ||
      coerceEFormFiles(raw).length > 0
        ? coerceEFormFiles(raw)
        : null;

    if (files) {
      if (!files.length) return <span className={colors.textMuted}>—</span>;
      return (
        <div
          className="flex flex-col gap-0.5 max-w-[13rem]"
          onClick={(e) => e.stopPropagation()}
        >
          {files.map((f) => (
            <button
              key={f.storageKey}
              type="button"
              className="text-left text-sm text-blue-600 hover:underline truncate"
              title={f.fileName}
              onClick={(e) => {
                e.stopPropagation();
                void openEFormFile(f.storageKey);
              }}
            >
              {f.fileName}
            </button>
          ))}
        </div>
      );
    }

    const title = formatCell(effective, raw);
    return (
      <span className="truncate max-w-[12rem] inline-block align-bottom" title={title}>
        {title}
      </span>
    );
  };

  if (definitionId == null) {
    return (
      <div className={cn('w-full py-4', colors.text)}>
        <div className="mb-6">
          <h1 className="text-xl font-semibold tracking-tight">Form submissions</h1>
          <p className={cn('text-sm mt-1', colors.textMuted)}>
            Select a published form to review its records.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {published.data.map((def) => (
            <Link
              key={def.id}
              to={`/e-forms/${def.id}/submissions`}
              className={cn(
                'group rounded-xl border p-4 transition-colors',
                colors.border,
                colors.surface,
                colors.interactiveHover,
              )}
            >
              <div className="flex items-start gap-3">
                <div
                  className={cn(
                    'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border',
                    colors.border,
                    colors.background,
                  )}
                >
                  {FormIcon}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate group-hover:text-blue-700 dark:group-hover:text-blue-300">
                    {def.name}
                  </div>
                  <div className={cn('text-xs mt-0.5 truncate', colors.textMuted)}>{def.code}</div>
                </div>
              </div>
            </Link>
          ))}
          {!published.isLoading && published.data.length === 0 ? (
            <div
              className={cn(
                'sm:col-span-2 xl:col-span-3 rounded-xl border border-dashed p-10 text-center text-sm',
                colors.border,
                colors.textMuted,
              )}
            >
              No published forms yet. Publish a form in the Form builder first.
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <GeneralList
      tabs={statusTabs.map(({ id, label, icon }) => ({ id, label, icon }))}
      activeTab={tab}
      onTabChange={setTab}
      sorting={{ sortConfig, onSort: handleSort }}
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: 'Search answers, submitter…',
      }}
      filters={
        filterFields.length ? (
          <div className="flex flex-wrap items-center gap-2">
            {filterFields.map((field) => (
              <div key={field.key} className="w-[14rem] max-w-full">
                <Select
                  name={`filter-${field.key}`}
                  value={fieldFilters[field.key] ?? ''}
                  onChange={(e) => {
                    const value = String(e.target.value);
                    setFieldFilters((prev) => {
                      const next = { ...prev };
                      if (!value) delete next[field.key];
                      else next[field.key] = value;
                      return next;
                    });
                  }}
                  options={filterOptionsFor(field)}
                  placeholder={filterAllLabel(field)}
                  showClearButton={false}
                  searchPlaceholder={`Search ${field.label}…`}
                  triggerClassName="!h-10 !min-h-10 !py-0 box-border"
                />
              </div>
            ))}
          </div>
        ) : null
      }
      actions={
        <div className="flex h-10 items-center gap-2">
          <ReportsDropdown definitionId={definitionId} items={savedReports.data || []} />
          <PrintsDropdown
            items={printouts}
            printing={printingTable}
            hasRows={Boolean(submissions.data.length)}
            onPrintTable={(template) => void runTablePrint(template)}
          />
          <button
            type="button"
            disabled={!submissions.data.length}
            onClick={() => void handleExport()}
            className={cn(
              'inline-flex h-10 items-center gap-1.5 rounded-xl border px-3.5 text-sm font-medium disabled:opacity-50',
              colors.border,
              colors.text,
              colors.interactiveHover,
            )}
          >
            <DownloadIcon className="h-4 w-4" />
            Export Excel
          </button>
          {canEdit ? (
            <Link
              to={`/e-forms/${definitionId}/fill`}
              className={cn(
                'inline-flex h-10 items-center gap-1.5 rounded-xl px-3.5 text-sm font-medium text-white shadow-sm',
                colors.primary,
              )}
            >
              <PlusIcon className="h-4 w-4" />
              New
            </Link>
          ) : null}
        </div>
      }
      hasFilters={hasFilters}
      onClearFilters={clearFilters}
      isLoading={submissions.isLoading}
      loadingLabel="Loading submissions…"
      isEmpty={!submissions.isLoading && submissions.data.length === 0}
      emptyState={{
        icon: <DocumentTextIcon className="h-12 w-12" />,
        title: hasFilters ? 'No matching submissions' : 'No submissions yet',
        hint: hasFilters
          ? 'Try a different search or clear the status filter.'
          : 'Fill this form to create the first record.',
        action: hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
              colors.border,
              colors.text,
            )}
          >
            Clear filters
          </button>
        ) : canEdit ? (
          <Link
            to={`/e-forms/${definitionId}/fill`}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-white',
              colors.primary,
            )}
          >
            <PlusIcon className="h-4 w-4" />
            New
          </Link>
        ) : undefined,
      }}
      pagination={{
        page,
        totalPages,
        total: submissions.total ?? 0,
        itemsPerPage,
        pageSizeOptions,
        onPageChange: setPage,
        onItemsPerPageChange: (size) => {
          setItemsPerPage(size);
          setPage(1);
        },
      }}
    >
      <table className="w-full text-sm">
        <thead>
          <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
            <th className={cn('sticky top-0 z-10 w-px whitespace-nowrap font-medium', colors.surface)} />
            {listColumns.map((col) =>
              col.kind === 'meta' && col.sortable ? (
                <GeneralListSortableTh key={col.key} sortKey={col.key} className="whitespace-nowrap">
                  {col.header}
                </GeneralListSortableTh>
              ) : (
                <th
                  key={col.key}
                  className={cn(
                    'sticky top-0 z-10 whitespace-nowrap font-medium',
                    colors.surface,
                    colors.textMuted,
                  )}
                >
                  {col.header}
                </th>
              ),
            )}
            <th className={cn('sticky top-0 z-10 w-full font-medium', colors.surface)} aria-hidden />
          </tr>
        </thead>
        <tbody>
          {submissions.data.map((row) => {
            const isDraft = row.status === 'DRAFT';
            const isSubmitted = row.status === 'SUBMITTED';
            const isLocked = Boolean(row.locked);
            return (
              <tr
                key={row.id}
                onClick={() => navigate(`/e-forms/submissions/${row.id}`)}
                className={cn(
                  'cursor-pointer border-b transition-colors last:border-b-0',
                  colors.border,
                  colors.text,
                  colors.interactiveHover,
                )}
              >
                <td className="w-px whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <div className="flex flex-nowrap items-center">
                    <Link
                      to={`/e-forms/submissions/${row.id}`}
                      title="Open"
                      aria-label="Open submission"
                      className={cn(
                        'rounded-lg p-1 transition-colors',
                        colors.primaryFg,
                        colors.interactiveHover,
                      )}
                    >
                      <EyeIcon className="h-5 w-5" />
                    </Link>
                    {canEdit && isDraft && !isLocked ? (
                      <button
                        type="button"
                        title="Submit"
                        aria-label="Submit"
                        onClick={(e) => runStatusAction(e, row, 'submit')}
                        className={cn(
                          'rounded-lg p-1 transition-colors',
                          isDark ? 'text-emerald-400' : 'text-emerald-600',
                          colors.interactiveHover,
                        )}
                      >
                        <CheckCircleIcon className="h-5 w-5" />
                      </button>
                    ) : null}
                    {canEdit && isSubmitted && !isLocked ? (
                      <button
                        type="button"
                        title="Revert to draft"
                        aria-label="Revert to draft"
                        onClick={(e) => runStatusAction(e, row, 'revert')}
                        className={cn(
                          'rounded-lg p-1 transition-colors',
                          colors.textMuted,
                          colors.interactiveHover,
                        )}
                      >
                        <UndoIcon className="h-5 w-5" />
                      </button>
                    ) : null}
                    {isLocked ? (
                      <span
                        className={cn('rounded-lg p-1 text-[10px] font-semibold uppercase', colors.textMuted)}
                        title="Approved — locked"
                      >
                        Lock
                      </span>
                    ) : null}
                    {canEdit && isDraft && !isLocked ? (
                      <Link
                        to={`/e-forms/submissions/${row.id}/edit`}
                        title="Edit"
                        aria-label="Edit submission"
                        className={cn(
                          'rounded-lg p-1 transition-colors',
                          colors.primaryFg,
                          colors.interactiveHover,
                        )}
                      >
                        <PencilIcon className="h-5 w-5" />
                      </Link>
                    ) : null}
                    {canDelete && isDraft && !isLocked ? (
                      <button
                        type="button"
                        title="Delete"
                        aria-label="Delete submission"
                        onClick={(e) => handleDelete(e, row)}
                        className={cn(
                          'rounded-lg p-1 transition-colors',
                          isDark ? 'text-red-400' : 'text-red-600',
                          colors.interactiveHover,
                        )}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    ) : null}
                  </div>
                </td>
                {listColumns.map((col) => (
                  <td key={col.key} className="whitespace-nowrap max-w-[14rem]">
                    {renderCell(col, row)}
                  </td>
                ))}
                <td aria-hidden />
              </tr>
            );
          })}
        </tbody>
      </table>
    </GeneralList>
  );
};

function ReportsDropdown({
  definitionId,
  items,
}: {
  definitionId: number;
  items: EFormSavedReport[];
}) {
  const { colors } = useTheme();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        className={cn(
          'inline-flex h-10 min-w-[8.5rem] items-center justify-between gap-2 rounded-xl border px-3.5 text-sm font-medium',
          colors.border,
          colors.text,
          colors.interactiveHover,
        )}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="flex items-center gap-1.5">
          <ChartTrendingUpIcon className="h-4 w-4" />
          Reports
        </span>
        <span className="flex items-center gap-1">
          <CountChip>{items.length}</CountChip>
          <ChevronDownIcon className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
        </span>
      </button>
      {open ? (
        <div
          role="listbox"
          className="absolute right-0 z-30 mt-1 w-72 rounded-lg border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-slate-900"
        >
          {items.length === 0 ? (
            <p className={cn('px-3 py-2 text-xs', colors.textMuted)}>No saved reports yet.</p>
          ) : (
            <ul className="max-h-64 space-y-0.5 overflow-y-auto">
              {items.map((row) => (
                <li key={row.id}>
                  <Link
                    to={`/e-forms/${definitionId}/reports/${row.id}`}
                    role="option"
                    className={cn(
                      'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-800',
                      colors.text,
                    )}
                    onClick={() => setOpen(false)}
                  >
                    <ChartTrendingUpIcon className={cn('h-4 w-4 shrink-0', colors.textMuted)} />
                    <span className="min-w-0 flex-1 truncate">{row.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

function PrintsDropdown({
  items,
  printing,
  hasRows,
  onPrintTable,
}: {
  items: EFormPrintTemplate[];
  printing: boolean;
  hasRows: boolean;
  onPrintTable: (template: EFormPrintTemplate) => void;
}) {
  const { colors } = useTheme();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        className={cn(
          'inline-flex h-10 min-w-[8.5rem] items-center justify-between gap-2 rounded-xl border px-3.5 text-sm font-medium',
          colors.border,
          colors.text,
          colors.interactiveHover,
        )}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="flex items-center gap-1.5">
          <DocumentTextIcon className="h-4 w-4" />
          {printing ? 'Printing…' : 'Prints'}
        </span>
        <span className="flex items-center gap-1">
          <CountChip>{items.length}</CountChip>
          <ChevronDownIcon className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
        </span>
      </button>
      {open ? (
        <div
          role="listbox"
          className="absolute right-0 z-30 mt-1 w-72 rounded-lg border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-slate-900"
        >
          {items.length === 0 ? (
            <p className={cn('px-3 py-2 text-xs', colors.textMuted)}>No printouts configured yet.</p>
          ) : (
            <ul className="max-h-64 space-y-0.5 overflow-y-auto">
              {items.map((template) => {
                const isTable = template.outputMode === 'table';
                const disabled = printing || (isTable && !hasRows);
                return (
                  <li key={template.id}>
                    {isTable ? (
                      <button
                        type="button"
                        role="option"
                        disabled={disabled}
                        className={cn(
                          'flex w-full items-start gap-2 rounded-md px-2.5 py-1.5 text-left text-sm hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-slate-800',
                          colors.text,
                        )}
                        onClick={() => {
                          onPrintTable(template);
                          setOpen(false);
                        }}
                      >
                        <DocumentTextIcon className={cn('mt-0.5 h-4 w-4 shrink-0', colors.textMuted)} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">{template.name}</span>
                          <span className={cn('block text-xs', colors.textMuted)}>
                            {printModeLabel(template.outputMode)}
                            {!hasRows ? ' · No records' : ''}
                          </span>
                        </span>
                      </button>
                    ) : (
                      <div
                        role="option"
                        className={cn(
                          'flex w-full items-start gap-2 rounded-md px-2.5 py-1.5 text-sm opacity-80',
                          colors.text,
                        )}
                      >
                        <DocumentTextIcon className={cn('mt-0.5 h-4 w-4 shrink-0', colors.textMuted)} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">{template.name}</span>
                          <span className={cn('block text-xs', colors.textMuted)}>
                            {printModeLabel(template.outputMode)} · Open a submission to print
                          </span>
                        </span>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default SubmissionsList;
